import { onBeforeMount, onBeforeUnmount, ref, unref, watch, type MaybeRef } from 'vue'
import { isCurrentYear } from '#shared/lib/datetime/is-current-year'
import { isToday } from '#shared/lib/datetime/is-today'
import { isYesterday } from '#shared/lib/datetime/is-yesterday'

const ONE_MINUTE_MS = 60_000
const ONE_HOUR_MS = 3_600_000

export function useTimeSinceString(date: MaybeRef<Date | string | null | undefined>) {
  const result = ref('')
  let updateTimeoutId: ReturnType<typeof setTimeout> | null = null

  interface ComputedDatetimeString {
    value: string
    nextChangeInMs: number | null
  }

  const compute = (date: Date | string): ComputedDatetimeString => {
    if (typeof date === 'string') date = new Date(date)

    const currentDate = new Date()
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)

    const elapsedTimeMs = currentDate.getTime() - date.getTime()

    if (elapsedTimeMs < ONE_MINUTE_MS) {
      return { value: 'Just now', nextChangeInMs: ONE_MINUTE_MS }
    }

    if (elapsedTimeMs < ONE_HOUR_MS) {
      const minutes = Math.floor(elapsedTimeMs / ONE_MINUTE_MS)
      if (minutes === 1) return { value: '1 minute ago', nextChangeInMs: ONE_MINUTE_MS }
      return { value: minutes + ' minutes ago', nextChangeInMs: ONE_MINUTE_MS }
    }

    const timeString = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    const tomorrowInMs = tomorrow.getTime() - currentDate.getTime()

    if (isToday(date)) {
      return { value: `Today at ${timeString}`, nextChangeInMs: tomorrowInMs }
    }

    if (isYesterday(date)) {
      return { value: `Yesterday at ${timeString}`, nextChangeInMs: tomorrowInMs }
    }

    if (isCurrentYear(date)) {
      const dateString = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })

      return { value: `${dateString} at ${timeString}`, nextChangeInMs: tomorrowInMs }
    }

    const dateString = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    return { value: `${dateString} at ${timeString}`, nextChangeInMs: null }
  }

  const update = () => {
    const dateVal = unref(date)
    if (!dateVal) return (result.value = '')

    const { value, nextChangeInMs } = compute(dateVal)
    result.value = value

    if (updateTimeoutId) {
      clearTimeout(updateTimeoutId)
    }

    if (nextChangeInMs) {
      updateTimeoutId = setTimeout(update, nextChangeInMs)
    }
  }

  watch(() => unref(date), update)

  onBeforeMount(update)

  onBeforeUnmount(() => {
    if (updateTimeoutId) {
      clearTimeout(updateTimeoutId)
    }
  })

  return result
}
